import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
} from '@mui/material';
import { User } from '../../user/types/type';
import { CreditAdjustment, TRANSACTION_TYPE_LABELS, TransactionType } from '../types';
import { formatCurrency } from '../utils';

interface CreditAdjustmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (adjustment: CreditAdjustment) => Promise<boolean>;
  selectedUser: User | null;
}

const CreditAdjustmentModal: React.FC<CreditAdjustmentModalProps> = ({
  open,
  onClose,
  onSubmit,
  selectedUser,
}) => {
  const [creditAmount, setCreditAmount] = useState<string>('');
  const [creditDescription, setCreditDescription] = useState<string>('');
  const [transactionType, setTransactionType] = useState<TransactionType>(TransactionType.Credit); 
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!selectedUser || !creditAmount || !creditDescription) {
      return;
    }
    setLoading(true);
    const success = await onSubmit({
      userId: selectedUser.userId,
      amount: parseFloat(creditAmount),
      type:transactionType, 
      description: creditDescription,
    });

    if (success) {
      handleClose();
    }
    setLoading(false);
  };

  const handleClose = () => {
    setCreditAmount('');
    setCreditDescription('');
    setTransactionType(TransactionType.Credit);
    onClose();
  };

  const isFormValid = creditAmount && creditDescription && selectedUser;
  const allowedTransactionTypes = [
    TransactionType.Credit,
    TransactionType.Debit,
    TransactionType.Reversal,
    TransactionType.Penalty,
    TransactionType.Promo,
  ];

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <DialogTitle sx={{
        pb: 2,
        fontSize: '18px',
        fontWeight: 500,
      }}>
        Credit Adjustment
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Type Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                minWidth: '80px',
                fontSize: '14px',
              }}
            >
              Type
            </Typography>
            <FormControl
              size="small"
              sx={{
                minWidth: '120px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  '& fieldset': {
                    border: 'none',
                  },
                },
              }}
            >
              <Select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value as TransactionType)}
                disabled={loading}
                displayEmpty
                sx={{
                  fontSize: '14px',
                }}
              >
                {allowedTransactionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {TRANSACTION_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Amount Field */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                minWidth: '80px',
                fontSize: '14px',
              }}
            >
              Amount
            </Typography>
            <TextField
              size="small"
              type="number"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="123.45"
              inputProps={{ min: 0, step: 0.01 }}
              disabled={loading}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>

          {/* Reason Field */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{
                minWidth: '80px',
                fontSize: '14px',
                pt: 1,
              }}
            >
              Reason
            </Typography>
            <TextField
              multiline
              rows={4}
              value={creditDescription}
              onChange={(e) => setCreditDescription(e.target.value)}
              placeholder="Typed text..."
              disabled={loading}
              sx={{
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  fontSize: '14px',
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        px: 3,
        pb: 3,
        pt: 1,
        gap: 2,
        justifyContent: 'flex-end',
      }}>
        <Button
          onClick={handleSubmit}
          variant="text"
          disabled={!isFormValid || loading}
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '14px',
            px: 2,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          {loading ? 'Processing...' : 'OK'}
        </Button>

        <Button
          onClick={handleClose}
          variant="text"
          disabled={loading}
          sx={{
            textTransform: 'uppercase',
            fontWeight: 600,
            fontSize: '14px',
            px: 2,
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreditAdjustmentModal;